import * as SVG from 'svg.js';

import { Directive, EventEmitter, HostListener, Inject, Output
       } from '@angular/core';
import { MatDialog } from '@angular/material';

import { isVolatileText, PlaceholderContext,
         PlaceholderKind, UnchangedPlaceholdersChoice,
         UnchangedPlaceholdersContext, UnchangedPlaceholdersDialogComponent
       } from 'app/dialogs/unchanged-placeholders-dialog/unchanged-placeholders-dialog.component'; // tslint:disable-line
import { StageService } from 'collage-designer/stage/stage.service';
import { GlobalConstants, ProgressConstants, StageConstants
       } from 'core/constants';
import { Globals } from 'core/globals';
import { StageUtils } from 'core/utils.stage';
import { ProgressService } from './progress.service';

import { ProductService } from 'core/product/product.service';
import { EventTrackingService, TrackingObject
       } from 'core/tracking/event-tracking.service';

export enum ValidationFailureReason
{
  PLACEHOLDERS_UNCHANGED,
}

interface ChangeableItem
{
  wrapper: SVG.Doc;
  placeholder: SVG.Element;
  kind: PlaceholderKind;
}

@Directive({
  selector: '[validate-design-step]'
})
export class ValidateDesignStepDirective
{
  private errorsOccurred: Array<String> = [];

  @Output() public validationDone: EventEmitter<boolean> = new EventEmitter();
  @Output() public validationFailed: EventEmitter<ValidationFailureReason> =
      new EventEmitter();

  constructor(
    private dialog: MatDialog,
    @Inject(ProgressService) private progressService: ProgressService,
    @Inject(StageService) private stageService: StageService,
    @Inject(ProductService) private productService: ProductService,
    @Inject(EventTrackingService) private eventTrackingService:
        EventTrackingService
  )
  {}

  @HostListener('click', ['$event'])
  public async checkPlaceholder(event):
      Promise<boolean>
  {
    const trackEvent = () => {
      const progressId = this.progressService.getNextProgress() ?
          this.progressService.getNextProgress().id : 'finalize';
      let trackingObject = <TrackingObject> {
        category: this.productService.getCurrentProduct().id,
        action: 'progress_change_start_' + progressId,
      };
      if (this.errorsOccurred.length > 0) {
        trackingObject.label = this.errorsOccurred.join('|');
      }
      this.eventTrackingService.track(trackingObject);
      this.errorsOccurred = [];
    };

    if (this.progressService.getCurrentProgress().group ===
        ProgressConstants.GROUP_COLLAGEDESIGNER)
    {
      if (! await this.getUnchangedPlaceholdersPromise()) {
        this.validationFailed.emit(
            ValidationFailureReason.PLACEHOLDERS_UNCHANGED);

        trackEvent();
        return false;
      }
    }

    trackEvent();
    this.validationDone.emit(true);
    return true;
  }

  private getChangeableItems(): Array<ChangeableItem>
  {
    const stage = this.stageService.getCurrentStageReference();

    if (! stage) {
      return [];
    }

    const toItem = (wrapper: SVG.Doc) => {
      const image = wrapper.select('image').first();
      const text = wrapper.select('text').first();

      return {
        wrapper: wrapper,
        placeholder: image || text,
        kind: image ? PlaceholderKind.IMAGE : PlaceholderKind.TEXT,
      };
    };

    return (stage as any)
      .select('*')
      .members
      .filter(el => StageUtils.getBooleanAttribute(
          el.node, StageConstants.ATTRIBUTE_CONTENT_CHANGEABLE))
      .map(toItem);
  }

  private buildPlaceholderContext(
      items: Array<ChangeableItem>): Array<PlaceholderContext>
  {
    const isUnchanged = it => StageUtils.getBooleanAttribute(
      it.placeholder.node, StageConstants.ATTRIBUTE_IS_UNCHANGED);

    const hintIfUnchanged = it => StageUtils.getBooleanAttribute(
      it.placeholder.node, StageConstants.ATTRIBUTE_HINT_IF_UNCHANGED, true);

    const analyzeItem = (it: ChangeableItem) => ({
      id: it.placeholder.attr('id'),
      kind: it.kind,
      textContent: it.kind === PlaceholderKind.TEXT
          ? (it.placeholder as SVG.Text).text()
          : undefined,
      node: it.placeholder.node,
    });

    return items
      .filter(isUnchanged)
      .filter(hintIfUnchanged)
      .map(analyzeItem);
  }

  private async getUnchangedPlaceholdersPromise(): Promise<boolean>
  {
    if (Globals.mode === GlobalConstants.MODE_GOD) {
      return true;
    }

    const changeableItems = this.getChangeableItems();

    const context: UnchangedPlaceholdersContext = {
      componentType: StageUtils.getComponentType(
          this.stageService.getCurrentStageReference()),
      placeholders: this.buildPlaceholderContext(changeableItems),
      changeableElementsCount: changeableItems.length,
    };

    if (context.placeholders.length < 1) {
      return true;
    }

    this.errorsOccurred.push('unchangedPlaceholders');

    const choice = await this.dialog
      .open(
          UnchangedPlaceholdersDialogComponent,
          {data: context, panelClass: 'dialog-pane'})
      .afterClosed()
      .toPromise();

    return this.evaluateUnchangedPlaceholdersChoice(context, choice);
  }

  private evaluateUnchangedPlaceholdersChoice(
    ctx: UnchangedPlaceholdersContext,
    choice: UnchangedPlaceholdersChoice): boolean
  {
    const clearPlaceholders = (placeholders: Array<PlaceholderContext>) => {
      for (const placeholder of placeholders) {
        this.stageService.clearElement(placeholder.id);
      }
    };

    switch (choice) {
      case UnchangedPlaceholdersChoice.FIX: return false;
      case UnchangedPlaceholdersChoice.IGNORE: // FALL THROUGH
      case UnchangedPlaceholdersChoice.IGNORE_IMAGES_CLEAR_TITLE: {
        clearPlaceholders(ctx.placeholders.filter(p => isVolatileText(ctx, p)));
        return true;
      }
      case UnchangedPlaceholdersChoice.CLEAR: {
        clearPlaceholders(ctx.placeholders);
        return true;
      }
      default: return false;
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/progress/validate-design-step.directive.ts